import * as types from 'src/store/mutation-types'
import { InvoiceTextTemplateClient } from '../../api/InvoiceTextTemplates'
import { Notify } from 'quasar'
import { createErrorFromResponse } from 'src/utils/error'
import i18n from 'src/i18n/i18n'

// initial state
const state = {
  invoiceTextTemplates: [],
  invoiceTextTemplatesMeta: {
    invoice_top: null,
    invoice_bottom: null
  },
  invoiceTextTemplatesLoaded: false
}

// getters
const getters = {
  invoiceTextTemplates: state => state.invoiceTextTemplates,
  invoiceTextTemplatesMeta: state => state.invoiceTextTemplatesMeta,
  invoiceTextTemplatesLoaded: state => state.invoiceTextTemplatesLoaded
}

const paginationDefault = {
  page: 1,
  rowsPerPage: 15,
  descending: true,
  sortBy: 'created_at',
  additionalIncludes: []
}

const actions = {
  listInvoiceTextTemplates ({ commit, state }, { pagination } = {}) {
    pagination = { ...paginationDefault, ...(pagination || {}) }

    const sort = [pagination.sortBy, pagination.descending ? 'desc' : 'asc']
    commit(types.SET_INVOICE_TEXT_TEMPLATES_LOADED, false)

    const client = filter => new InvoiceTextTemplateClient()
      .include(pagination.additionalIncludes)
      .sort(sort)
      .limit(pagination.rowsPerPage)
      .page(pagination.page)
      .filter(filter)
      .list()

    return Promise.all(Object.keys(state.invoiceTextTemplatesMeta)
      .map(type => client(['type', '=', type])))
      .then(responses => {
        const templates = [...responses[0].data.data, ...responses[1].data.data]

        const meta = {
          invoice_top: responses[0].data.meta,
          invoice_bottom: responses[1].data.meta
        }

        commit(types.SET_INVOICE_TEXT_TEMPLATES, templates)
        commit(types.SET_INVOICE_TEXT_TEMPLATES_META, meta)
        commit(types.SET_INVOICE_TEXT_TEMPLATES_LOADED, true)

        return {
          invoiceTextTemplates: state.invoiceTextTemplates,
          meta: state.invoiceTextTemplatesMeta
        }
      })
  },

  fetchNextInvoiceTextTemplateChunk ({ commit, state }, { done, pagination = {}, type }) {
    if (!state.invoiceTextTemplatesLoaded) {
      return done()
    } else if (!state.invoiceTextTemplatesMeta) {
      return done(true)
    }

    if (!type) {
      throw new Error('Type is required')
    }

    pagination = { ...paginationDefault, ...(pagination || {}) }
    const sort = [pagination.sortBy, pagination.descending ? 'desc' : 'asc']

    const filter = ['type', '=', type]

    let currentPage = state.invoiceTextTemplatesMeta[type].pagination.current_page
    let totalPages = state.invoiceTextTemplatesMeta[type].pagination.total_pages

    // Check if there is still a chunk to fetch available.
    if (currentPage < totalPages) {
      let client = new InvoiceTextTemplateClient()
        .include(pagination.additionalIncludes)
        .sort(sort)
        .limit(pagination.rowsPerPage)
        .page(currentPage + 1)
        .filter(filter)

      return client
        .list()
        .then(response => {
          let newInvoiceTextTemplates = state.invoiceTextTemplates.concat(response.data.data)

          let meta = Object.assign({}, state.invoiceTextTemplatesMeta)
          meta[type] = response.data.meta

          commit(types.SET_INVOICE_TEXT_TEMPLATES, newInvoiceTextTemplates)
          commit(types.SET_INVOICE_TEXT_TEMPLATES_META, meta)

          // Indicate that data has been loaded successfully.
          done()
        })
    } else {
      // No more data available to fetch.
      // Passing true to the callback function tells the infinite scroll component to stop loading.
      done(true)
    }
  },

  createInvoiceTextTemplate ({ commit, state }, invoiceTextTemplate) {
    return new InvoiceTextTemplateClient()
      .create(invoiceTextTemplate)
      .then(response => commit(types.ADD_INVOICE_TEXT_TEMPLATE, response.data.data))
  },

  updateInvoiceTextTemplate ({ commit, state }, { template, id }) {
    return new InvoiceTextTemplateClient()
      .update(id, template)
      .then(response => commit(types.UPDATE_INVOICE_TEXT_TEMPLATE, response.data.data))
  },

  deleteInvoiceTextTemplate ({ commit, state }, invoiceTextTemplate) {
    const type = invoiceTextTemplate.type

    return new InvoiceTextTemplateClient()
      .destroy(invoiceTextTemplate.id)
      .then(() => commit(types.REMOVE_INVOICE_TEXT_TEMPLATE, invoiceTextTemplate))
      .then(() => {
        const meta = JSON.parse(JSON.stringify(state.invoiceTextTemplatesMeta))
        meta[type].pagination = {
          total: state.invoiceTextTemplatesMeta[type].pagination.total - 1
        }

        commit(types.SET_INVOICE_TEXT_TEMPLATES_META, meta)
      })
      .then(() => Notify.create({ message: i18n.tc('invoice_text_template_deleted'), type: 'positive' }))
      .then(() => Promise.resolve({
        invoiceTextTemplates: state.invoiceTextTemplates,
        meta: state.invoicesMeta
      }))
      .catch(createErrorFromResponse)
  }
}

// mutations
const mutations = {
  [types.SET_INVOICE_TEXT_TEMPLATES] (state, templates) {
    state.invoiceTextTemplates = templates
  },

  [types.SET_INVOICE_TEXT_TEMPLATES_META] (state, templateMeta) {
    state.invoiceTextTemplatesMeta = templateMeta
  },

  [types.SET_INVOICE_TEXT_TEMPLATES_LOADED] (state, loaded) {
    state.invoiceTextTemplatesLoaded = loaded
  },

  [types.ADD_INVOICE_TEXT_TEMPLATE] (state, template) {
    state.invoiceTextTemplates.unshift(template)
  },

  [types.UPDATE_INVOICE_TEXT_TEMPLATE] (state, newTemplate) {
    let templateIndex = state.invoiceTextTemplates.findIndex(template => template.id === newTemplate.id)
    state.invoiceTextTemplates.splice(templateIndex, 1, newTemplate)
  },

  [types.REMOVE_INVOICE_TEXT_TEMPLATE] (state, template) {
    const toDelete = state.invoiceTextTemplates.find(stateTemplate => stateTemplate.id === template.id)
    const index = state.invoiceTextTemplates.indexOf(toDelete)

    if (index !== -1) {
      state.invoiceTextTemplates.splice(index, 1)
    }
  }
}

export default {
  state,
  getters,
  actions,
  mutations
}
